import { useEffect, useRef, useState } from 'react'
import { Loader2, Trash2, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useDeleteAvatar, useUploadAvatar } from '@/features/users/use-users'
import { AvatarCropDialog } from './avatar-crop-dialog'
import { AvatarImage } from './avatar-image'

interface AvatarUploadBlockProps {
  handle: string
  displayName: string
  avatarUrl: string | null
}

/**
 * Phase 7 — Avatar upload + remove block on `/settings/profile`. Drag-and-drop / click-to-pick
 * → AvatarCropDialog → upload mutation. Remove triggers the delete mutation.
 *
 * <p>The 5 MB cap and format whitelist are enforced server-side; the client only filters the
 * file picker via `accept` (UX hint, not security).
 */
export const AvatarUploadBlock = ({ handle, displayName, avatarUrl }: AvatarUploadBlockProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)

  const upload = useUploadAvatar()
  const remove = useDeleteAvatar()

  // Clean up the object URL when the dialog closes or the component unmounts.
  useEffect(() => {
    return () => {
      if (imageSrc) URL.revokeObjectURL(imageSrc)
    }
  }, [imageSrc])

  const handlePickedFile = (file: File | null | undefined) => {
    if (!file) return
    setImageSrc(URL.createObjectURL(file))
  }

  const onConfirmCrop = (blob: Blob) => {
    upload.mutate(blob, {
      onSuccess: () => {
        toast.success('Avatar updated')
        setImageSrc(null)
      },
      onError: (err) => {
        const apiErr = err as { detail?: string; status?: number }
        toast.error(apiErr.detail ?? 'Could not upload avatar')
      },
    })
  }

  const onRemove = () => {
    remove.mutate(undefined, {
      onSuccess: () => toast.success('Avatar removed'),
      onError: () => toast.error('Could not remove avatar'),
    })
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Avatar</p>
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          handlePickedFile(e.dataTransfer.files?.[0])
        }}
        className={
          'flex flex-wrap items-center gap-4 rounded-md border border-dashed p-4 transition-colors ' +
          (dragOver ? 'border-primary bg-primary/5' : 'border-input bg-muted/20')
        }
      >
        <AvatarImage avatarUrl={avatarUrl} handle={handle} displayName={displayName} size={96} />
        <div className="flex-1 space-y-2">
          <p className="text-xs text-muted-foreground">
            Drop an image here, or click Upload. PNG, JPEG, or WebP. Max 5 MB.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={upload.isPending}
              className="gap-2"
            >
              {upload.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {upload.isPending ? 'Uploading…' : 'Upload'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onRemove}
              disabled={!avatarUrl || remove.isPending}
              className="gap-2 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
              Remove
            </Button>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => {
            handlePickedFile(e.target.files?.[0])
            // Reset so picking the same file again still re-fires onChange.
            e.target.value = ''
          }}
        />
      </div>

      <AvatarCropDialog
        open={imageSrc !== null}
        imageSrc={imageSrc}
        pending={upload.isPending}
        onCancel={() => setImageSrc(null)}
        onConfirm={onConfirmCrop}
      />
    </div>
  )
}
